/**
 * =============================================================================
 * IDS CRM Web - CountrySelect
 * =============================================================================
 *
 * The shared country picker. Every address form uses this so the vocabulary
 * can never drift again (it used to: three forms, three different lists).
 *
 * It is SearchSelect in combobox mode rather than a native <select>, because
 * the catalogue is the full ISO set. A 249-option <select> is a wheel picker
 * on iOS and an unsearchable full-screen list on Android, and even on desktop
 * only jumps by typed prefix. Here, focusing opens the whole list with the
 * operating countries pinned at the top — so the everyday one-click case
 * survives — and typing filters the lot.
 *
 * Filtering ranks an exact ISO code first, then names that start with the
 * term, then names that merely contain it. "de" therefore offers Germany
 * before Sweden.
 *
 * onChange receives a synthetic { target: { id, name, value } }, so the
 * `name`-based handleChange handlers the forms already use keep working, as
 * do the `e => set('country_code', e.target.value)` inline ones.
 *
 * =============================================================================
 */

import React, { useCallback, useMemo } from 'react';
import SearchSelect from './SearchSelect';
import { COUNTRIES, COUNTRY_NAMES, COMMON_COUNTRY_CODES, normaliseCountryCode } from '../utils/countries';

const PINNED_GROUP = 'Operating countries';
const ALL_GROUP = 'All countries';

const getOptionId = (row) => row.code;
const getOptionLabel = (row) => row.name;
const getOptionSub = (row) => row.code;
const getOptionGroup = (row) => row.group;

/**
 * Match rank, lowest first; -1 means no match. Ordering within a rank is left
 * to the catalogue's own A-Z order (Array#sort is stable).
 */
function matchRank(country, term) {
  const name = country.name.toLowerCase();
  if (country.code.toLowerCase() === term) return 0;
  if (name.startsWith(term)) return 1;
  if (name.includes(term)) return 2;
  return -1;
}

function CountrySelect({
  id,
  name = 'country_code',
  value,
  onChange,
  disabled = false,
  required = false,
  className = '',
  commonCodes = COMMON_COUNTRY_CODES,
  placeholder = 'Select or type a country'
}) {
  const current = normaliseCountryCode(value);

  // The unfiltered list: operating countries first, then everyone else. Unlike
  // the old <select> the pinned rows are not repeated below — with a filter to
  // hand, the duplicate that helped A-Z hunting would just read as a bug.
  const browseList = useMemo(() => {
    const pinned = commonCodes
      .map(code => normaliseCountryCode(code))
      .filter(code => COUNTRY_NAMES[code]);
    const isPinned = new Set(pinned);
    return [
      ...pinned.map(code => ({ code, name: COUNTRY_NAMES[code], group: PINNED_GROUP })),
      ...COUNTRIES.filter(country => !isPinned.has(country.code))
        .map(country => ({ ...country, group: ALL_GROUP }))
    ];
  }, [commonCodes]);

  const fetchOptions = useCallback((rawTerm) => {
    const term = String(rawTerm || '').trim().toLowerCase();
    if (!term) return browseList;
    // No group headings once filtered — a heading per match is just noise.
    return COUNTRIES
      .map(country => ({ country, rank: matchRank(country, term) }))
      .filter(entry => entry.rank >= 0)
      .sort((a, b) => a.rank - b.rank)
      .map(entry => ({ ...entry.country, group: null }));
  }, [browseList]);

  // Historic imports carry codes no dropdown ever offered; keep showing such a
  // value rather than letting the picker silently present another country.
  const label = COUNTRY_NAMES[current] || (current ? `${current} (unrecognised code)` : '');

  const handleSelect = useCallback((row) => {
    onChange({ target: { id, name, value: row ? row.code : '' } });
  }, [onChange, id, name]);

  return (
    <div className={className}>
      <SearchSelect
        variant="combobox"
        id={id}
        value={current}
        initialLabel={label}
        placeholder={placeholder}
        disabled={disabled}
        required={required}
        fetchOptions={fetchOptions}
        getOptionId={getOptionId}
        getOptionLabel={getOptionLabel}
        getOptionSub={getOptionSub}
        getOptionGroup={getOptionGroup}
        onSelect={handleSelect}
        selectedIndicator={null}
      />
      {/* Keeps the ISO code in the DOM for anything that serialises the form. */}
      <input type="hidden" name={name} value={current} />
    </div>
  );
}

export default CountrySelect;
